import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ActionButton from './ActionButton';
import UploadDropzone from './UploadDropzone';
import UploadedFileRow from './UploadedFileRow';
const UPLOAD_STATUS = {
    IDLE: 'IDLE',
    UPLOADING: 'UPLOADING',
    COMPLETE: 'COMPLETE',
    FAILED: 'FAILED'
};
const UploadStep = ({ uploadDeckUrl, maxFileSizeMb, acceptedExtensions, onCancel, onContinue }) => {
    const { t } = useTranslation();
    const acceptAttr = acceptedExtensions.map((ext) => `.${ext}`).join(',');
    const [fileName, setFileName] = useState('');
    const [status, setStatus] = useState(UPLOAD_STATUS.IDLE);
    const [progress, setProgress] = useState(0);
    const [etaLabel, setEtaLabel] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [deckRef, setDeckRef] = useState(null);
    const buildEtaLabel = (loaded, total, startTime) => {
        const elapsedSeconds = (Date.now() - startTime) / 1000;
        const rate = elapsedSeconds > 0 ? loaded / elapsedSeconds : 0;
        if (rate <= 0) {
            return '';
        }
        const remainingSeconds = (total - loaded) / rate;
        if (remainingSeconds >= 60) {
            return t('pitchCreation.upload.etaMinutes', {
                minutes: Math.ceil(remainingSeconds / 60)
            });
        }
        return t('pitchCreation.upload.etaSeconds', {
            seconds: Math.ceil(remainingSeconds)
        });
    };
    const validate = (file) => {
        var _a, _b;
        const extension = (_b = (_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : '';
        if (!acceptedExtensions.includes(extension)) {
            return t('pitchCreation.upload.errorWrongType');
        }
        if (file.size > maxFileSizeMb * 1024 * 1024) {
            return t('pitchCreation.upload.errorTooLarge');
        }
        return '';
    };
    const mapServerError = (error) => {
        var _a, _b, _c;
        const code = (_c = (_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.code;
        if (code === 'FILE_TOO_LARGE') {
            return t('pitchCreation.upload.errorTooLarge');
        }
        if (code === 'INVALID_TYPE') {
            return t('pitchCreation.upload.errorWrongType');
        }
        return t('pitchCreation.upload.errorGeneric');
    };
    const upload = (file) => {
        setStatus(UPLOAD_STATUS.UPLOADING);
        setProgress(0);
        setEtaLabel('');
        setErrorMessage('');
        setDeckRef(null);
        const formData = new FormData();
        formData.append('deck', file);
        const startTime = Date.now();
        axios
            .post(uploadDeckUrl, formData, {
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            onUploadProgress: (event) => {
                if (!event.total) {
                    return;
                }
                setProgress(Math.round((event.loaded / event.total) * 100));
                setEtaLabel(buildEtaLabel(event.loaded, event.total, startTime));
            }
        })
            .then((response) => {
            setDeckRef(response.data.deckRef);
            setProgress(100);
            setStatus(UPLOAD_STATUS.COMPLETE);
        })
            .catch((error) => {
            setProgress(0);
            setStatus(UPLOAD_STATUS.FAILED);
            setErrorMessage(mapServerError(error));
        });
    };
    const handleFileSelected = (file) => {
        // Ignore new picks while an upload is in flight so two uploads never race.
        if (status === UPLOAD_STATUS.UPLOADING) {
            return;
        }
        setFileName(file.name);
        const validationError = validate(file);
        if (validationError) {
            setStatus(UPLOAD_STATUS.FAILED);
            setProgress(0);
            setEtaLabel('');
            setDeckRef(null);
            setErrorMessage(validationError);
            return;
        }
        upload(file);
    };
    const handleRemove = () => {
        setFileName('');
        setStatus(UPLOAD_STATUS.IDLE);
        setProgress(0);
        setEtaLabel('');
        setErrorMessage('');
        setDeckRef(null);
    };
    const handleContinue = () => {
        if (status === UPLOAD_STATUS.COMPLETE && deckRef) {
            onContinue(deckRef);
        }
    };
    return (React.createElement("section", { className: "mx-auto w-full max-w-3xl rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8" },
        React.createElement("h1", { className: "text-2xl font-semibold text-gray-800" }, t('pitchCreation.upload.title')),
        React.createElement("p", { className: "mt-2 text-gray-700" }, t('pitchCreation.upload.body')),
        React.createElement("div", { className: "mt-6" },
            React.createElement(UploadDropzone, { accept: acceptAttr, onFileSelected: handleFileSelected })),
        status !== UPLOAD_STATUS.IDLE && (React.createElement("div", { className: "mt-4" },
            React.createElement(UploadedFileRow, { fileName: fileName, status: status, percentage: progress, etaLabel: etaLabel, errorMessage: errorMessage, onRemove: handleRemove }))),
        React.createElement("div", { className: "mt-8 flex justify-end gap-3" },
            React.createElement(ActionButton, { variant: "muted", label: t('pitchCreation.upload.cancel'), onClick: onCancel }),
            React.createElement(ActionButton, { label: t('pitchCreation.upload.continue'), onClick: handleContinue, disabled: status !== UPLOAD_STATUS.COMPLETE }))));
};
export default UploadStep;
